import React, { FC, useEffect, useState, ReactNode } from 'react';
import styled from '@emotion/styled';
import { Accordion } from '~/shared/components/Accordion/Accordion';
import { AccordionItem } from '~/shared/components/Accordion/components/AccordionItem/AccordionItem';
import { Chevron } from './Faq';
import ChevronIcon from '~/icons/chevron-down.svg';
import Flex from '../../Flex';

const Content = styled.div(() => ({
    background: 'inherit',
    textAlign: 'center',
}));

const AccordionTitle = styled.h2(({ theme }) => ({
    color: theme.colors.black,
    fontSize: theme.fontSizes['xs'],
    fontWeight: theme.fontWeights.bold,
    textTransform: 'none',
    fontFamily: theme.fonts.roboto,

    '&::first-letter': {
        textTransform: 'capitalize',
    },
}));

const AccordionTitleAddon = styled.span(({ theme }) => ({
    ...theme.mixins.useTextStyle('caption'),
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.space[2],
    color: theme.colors.black,
    backgroundColor: 'transparent',
    marginLeft: theme.space[4],
    padding: `${theme.space[1]} ${theme.space[2]}`,
    borderRadius: theme.sizes.cornerRadius,
    border: `1px solid ${theme.colors.black}`,
    height: theme.space[4],

    '&::before': {
        content: '""',
        display: 'inline-block',
        backgroundColor: theme.colors.brandOrangeMedium,
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        flexShrink: 0,
    },
}));

type Props = {
    id?: string;
    title: string;
    titleAddon?: ReactNode;
    initOpen?: boolean;
    onToggle?: (isOpen: boolean, accordion: string) => void;
    initOpenForSEO?: boolean;
};

export const AccordionSection: FC<Props> = ({
    children,
    title,
    titleAddon,
    initOpen,
    onToggle,
    id,
    initOpenForSEO,
}) => {
    const isInitialOpen = initOpen || initOpenForSEO;
    const [isOpen, setIsOpen] = useState(isInitialOpen);

    const handleAccordion = () => {
        onToggle && onToggle(!isOpen, title);
        setIsOpen(!isOpen);
    };

    useEffect(() => {
        if (initOpenForSEO) {
            // close immediately after component render
            setIsOpen(false);
        }
    }, []);

    return (
        <Accordion
            defaultValue={isInitialOpen && isOpen ? id : undefined}
            onValueChange={handleAccordion}
        >
            <AccordionItem
                id={id}
                initAsOpen={isOpen}
                icon={({ isOpen }) => (
                    <Chevron isOpen={isOpen}>
                        <ChevronIcon />
                    </Chevron>
                )}
                header={
                    <Flex alignItems="center">
                        <AccordionTitle>{title}</AccordionTitle>
                        {titleAddon && (
                            <AccordionTitleAddon aria-hidden="true">
                                {titleAddon}
                            </AccordionTitleAddon>
                        )}
                    </Flex>
                }
            >
                <Content>{children}</Content>
            </AccordionItem>
        </Accordion>
    );
};
